import * as React from "react";
import { styled, Typography, SxProps, Theme, Box } from "@mui/material";
import Cover from "../../Misc/Cover";
import { Link, To } from "react-router";
import { CardCover, CardCovers, CardCoverWrapper } from "../WishlistCollectionCard/wishlist-collection-card";
import { hexToRgbString } from "../../../Utils/Helpers/cssUtils";

export type CollectionCardProps = {
  className?: string;
  sx?: SxProps;
  to: To;
  covers?: string[];
  title: string;
  placeholder?: boolean;
  nbitem?: number;
};

const CollectionCard: React.FC<CollectionCardProps> = (props) => {
  const {
    covers = ["/static/images/collection-card-cover-fallback.png"],
    title,
    to,
    placeholder,
    nbitem,
    ...otherProps
  } = props;
  const _cover = placeholder ? "/static/images/collection-card-cover-fallback.png" : covers[0];
  const rotatedPicture = placeholder ? "/static/images/collection-card-cover-fallback-rotated.png" : covers[0];

  const cardContent = (
    <>
      <Cover img={{ src: _cover, alt: title }} className="cover" />
      {covers.length >= 1 ? (
        <div
          style={{
            position: "absolute",
            top: -34,
            right: -18,
            borderRadius: 2,
            transform: "rotate(8deg)",
            width: 142,
            height: 200,
          }}
        >
          <CardCovers small={true}>
            {covers &&
              covers.map((cover) =>
                cover !== "" ? (
                  <CardCoverWrapper
                    small={true}
                    key={cover}
                    justifyContent="center"
                    alignItems="center"
                    nbtitles={nbitem ?? 1}
                  >
                    <CardCover src={cover} alt="" />
                  </CardCoverWrapper>
                ) : null
              )}
          </CardCovers>
        </div>
      ) : (
        <RotatedPicture src={rotatedPicture} alt={title} />
      )}
      <Typography variant="h3" fontWeight={700} dangerouslySetInnerHTML={{ __html: title }} />
    </>
  );

  return placeholder ? (
    <StyledBox {...otherProps}>{cardContent}</StyledBox>
  ) : (
    <StyledLink to={props.to} {...otherProps}>
      {cardContent}
    </StyledLink>
  );
};

export const wrapperStyles = ({ theme }: { theme: Theme }) =>
  ({
    position: "relative",
    display: "flex",
    alignItems: "flex-end",
    width: 240,
    height: 240,
    padding: theme.spacing(3),
    borderRadius: 10,
    boxSizing: "border-box",
    overflow: "hidden",
    background: `radial-gradient(102.68% 102.68% at 102.68% 0%, rgba(${hexToRgbString(
      theme.palette.primary.main
    )}, 0.32) 0%, rgba(${hexToRgbString(theme.palette.primary.main)}, 0.12) 53.4%, rgba(${hexToRgbString(
      theme.palette.primary.main
    )}, 0.12) 100%)`,

    h3: {
      position: "relative",
      zIndex: 1,
      color: theme.palette.text.primary,
    },
  } as const);

const StyledBox = styled(Box)(({ theme }) => ({
  ...wrapperStyles({ theme }),
}));

const StyledLink = styled(Link)(({ theme }) => ({
  ...wrapperStyles({ theme }),
  "&:hover": {
    "&:after": {
      opacity: 0.6,
    },
  },
  ".cover": {
    filter: "blur(64px)",
  },
  "&:after": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    content: '""',
    background: "linear-gradient(0deg, rgba(0, 0, 0, 0.64), rgba(0, 0, 0, 0.64))",
    transition: "opacity .3s",
    opacity: 0,
    pointerEvents: "none",
  },
}));

const RotatedPicture = styled("img")(({ theme }) => ({
  position: "absolute",
  top: -34,
  right: -18,
  borderRadius: 2,
  transform: "rotate(8deg)",
  width: 142,
  height: 200,
}));

export default CollectionCard;
